import {Component, OnInit, OnDestroy, QueryList, ElementRef, ViewChildren, ViewChild} from '@angular/core';
import {Api} from '@shared/constants/common-field.constants';
import {CommandService} from '@core/services/command.service';
import {PlatformQueryService} from '@core/services/platform-query.service';
import {PlatformQuery} from '@shared/models/platform-query/platform-query';
import {take, takeUntil} from 'rxjs/operators';
import {PlatformCommand} from '@shared/commands/command-base';
import {Subject} from 'rxjs';
import {SnackBarService} from '@core/services/snack-bar.service';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';

@Component({
    standalone: false,
    selector: 'app-scenario-dynamic-items',
  templateUrl: './scenario-dynamic-items.component.html',
  styleUrl: './scenario-dynamic-items.component.scss'
})
export class ScenarioDynamicItemsComponent implements OnInit, OnDestroy {
  @ViewChildren('optionItem') optionItems!: QueryList<ElementRef>;
  @ViewChild('newItemInput') newItemInputRef!: ElementRef;

  selectedKey = '';
  data: ScenarioDynamicItemsModel | null = null;
  itemGroups: ScenarioDynamicItemGroupModel[] = [];
  currentItems: ScenarioOption[] = [];

  // UI Properties
  isOptionEditing = false;
  editingIndex = -1;
  exitingText = '';
  editingStates: boolean[] = [];

  private destroy$ = new Subject<void>();
  private hasChanges = false;

  constructor(
    private platformQueryService: PlatformQueryService,
    private commandService: CommandService,
    private snackBarService: SnackBarService,
    private fuseSplashScreenService: FuseSplashScreenService
  ) {}

  ngOnInit(): void {
    this.loadScenarioDynamicItems();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadScenarioDynamicItems(): void {
    const query = new PlatformQuery();
    query.name = 'ScenarioDynamicItemsQuery';
    query.api = Api.Spreadsheet;

    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      this.data = response.fieldValues['ScenarioDynamicItem'] as ScenarioDynamicItemsModel;
      this.itemGroups = this.data?.itemGroups || [];

      if (this.itemGroups.length > 0 && this.selectedKey === '') {
        this.selectedKey = this.itemGroups[0].title;
        this.updateCurrentItems();
      }
    });
  }

  selectTab(key: string): void {
    this.selectedKey = key;
    this.updateCurrentItems();
    this.resetEditing();
  }

  private updateCurrentItems(): void {
    const currentGroup = this.itemGroups.find(group => group.title === this.selectedKey);
    this.currentItems = currentGroup?.items || [];
    this.editingStates = new Array(this.currentItems.length).fill(false);
  }

  startEditItem(index: number): void {
    if (!this.currentItems[index].isCustom) return;

    this.resetEditing();
    this.editingIndex = index;
    this.editingStates[index] = true;
    this.exitingText = this.currentItems[index].text;

    setTimeout(() => {
      const el = this.optionItems.toArray()[index];
      el?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  removeOption(index: number): void {
    if (!this.currentItems[index].isCustom) {
      this.snackBarService.showError('You cannot remove a default item.');
      return;
    }

    this.currentItems.splice(index, 1);
    this.editingStates.splice(index, 1);
    this.hasChanges = true;
  }

  moveOption(index: number, direction: string): void {
    if (direction === 'up' && index > 0) {
      [this.currentItems[index], this.currentItems[index - 1]] =
        [this.currentItems[index - 1], this.currentItems[index]];
      this.hasChanges = true;
    } else if (direction === 'down' && index < this.currentItems.length - 1) {
      [this.currentItems[index], this.currentItems[index + 1]] =
        [this.currentItems[index + 1], this.currentItems[index]];
      this.hasChanges = true;
    }
  }

  resetEditing(): void {
    this.isOptionEditing = false;
    this.editingIndex = -1;
    this.editingStates.fill(false);
    this.exitingText = '';
  }

  onSaveOption(event: { text: string }): void {
    const newText = event.text?.trim();
    if (!newText) return;

    if (this.editingIndex >= 0) {
      this.currentItems[this.editingIndex].text = newText;
    } else {
      this.currentItems.push({
        text: newText,
        isChecked: false,
        isCustom: true
      });
      this.editingStates.push(false);
    }

    this.hasChanges = true;
    this.resetEditing();
  }

  addOption(): void {
    this.resetEditing();
    this.isOptionEditing = true;
    this.exitingText = '';

    setTimeout(() => {
      this.newItemInputRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  saveScenarioDynamicItemsModel(): void {
    const command = new PlatformCommand('SaveScenarioDynamicItemsCommand', Api.Spreadsheet);
    command.fieldValues['ScenarioDynamicItem'] = this.data;

    this.fuseSplashScreenService.show();

    this.commandService.executePlatformCommand(command).pipe(takeUntil(this.destroy$)).subscribe(
      response => {
        this.fuseSplashScreenService.hide();
        this.snackBarService.showInformation('Dynamic items saved!');
        this.hasChanges = false;
      },
      error => {
        this.fuseSplashScreenService.hide();
        this.snackBarService.showError('Dynamic items save failed!');
      }
    );
  }

  onOptionChecked(item: ScenarioOption): void {
    this.hasChanges = true;
  }

  get showSaveButton(): boolean {
    return this.hasChanges;
  }
}
